import React, { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Group,
  Button,
  Text,
  Image,
  Checkbox,
  Loader,
  Alert,
  SimpleGrid,
  Paper,
  UnstyledButton,
  Center,
  Badge,
  Divider,
  ActionIcon,
  Collapse,
} from '@mantine/core';
import {
  IconBrandPinterest,
  IconChevronLeft,
  IconChevronDown,
  IconAlertCircle,
  IconCheck,
} from '@tabler/icons-react';
import { User } from 'firebase/auth';
import { initiatePinterestConnection } from '../../../utils/pinterestAuth';
import { notifications } from '@mantine/notifications';
import { handleUsageLimitError } from '../../../utils/usageLimitNotifications';

type Board = {
  id: string;
  name: string;
  pinCount: number;
  thumbnailUrl: string | null;
};

type Pin = {
  id: string;
  title: string | null;
  description: string | null;
  link: string;
  thumbnailUrl: string | null;
  alreadyImported?: boolean;
};

type Section = {
  id: string;
  name: string;
};

type SectionPins = {
  section: Section | null; // null = unsectioned pins
  pins: Pin[];
  bookmark: string | null;
  loading: boolean;
};

type PinterestImportProps = {
  user: User;
  pinterestConnected: boolean;
  onImportStarted: () => void;
  onClose: () => void;
  active?: boolean;
};

export const PinterestImport: React.FC<PinterestImportProps> = ({
  user,
  pinterestConnected,
  onImportStarted,
  onClose,
  active = true,
}) => {
  const [connecting, setConnecting] = useState(false);
  const [boards, setBoards] = useState<Board[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
  const [sectionGroups, setSectionGroups] = useState<SectionPins[]>([]);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [selectedPinIds, setSelectedPinIds] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const fetchBoards = useCallback(async () => {
    setLoadingBoards(true);
    setError(null);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pinterest/boards`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Pinterest connection expired. Please reconnect.');
          return;
        }
        if (response.status === 429) return; // Silently handle rate limit
        throw new Error('Failed to load boards');
      }

      const data = await response.json();
      setBoards(data.boards);
    } catch (err) {
      setError('Failed to load Pinterest boards. Please try again.');
      console.error('Error fetching boards:', err);
    } finally {
      setLoadingBoards(false);
    }
  }, [user]);

  const fetchPinsForGroup = useCallback(async (
    boardId: string,
    sectionId: string | null,
    bookmark?: string
  ): Promise<{ pins: Pin[]; bookmark: string | null }> => {
    const token = await user.getIdToken();
    const basePath = sectionId
      ? `/api/pinterest/boards/${boardId}/sections/${sectionId}/pins`
      : `/api/pinterest/boards/${boardId}/pins`;
    const url = new URL(`${process.env.REACT_APP_API_URL}${basePath}`);
    if (bookmark) url.searchParams.set('bookmark', bookmark);

    const response = await fetch(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.status === 429) return { pins: [], bookmark: null };
    if (!response.ok) throw new Error('Failed to load pins');
    return response.json();
  }, [user]);

  const loadBoardContent = useCallback(async (board: Board) => {
    setLoadingInitial(true);
    setError(null);
    try {
      const token = await user.getIdToken();

      // Fetch sections and unsectioned pins in parallel
      const [sectionsRes, unsectionedRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/pinterest/boards/${board.id}/sections`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r.ok ? r.json() : { sections: [] }),
        fetchPinsForGroup(board.id, null),
      ]);

      const sections: Section[] = sectionsRes.sections || [];
      const groups: SectionPins[] = [];

      // Fetch section pins first so we can exclude them from the board-level results
      const sectionPinIds = new Set<string>();
      if (sections.length > 0) {
        const sectionResults = await Promise.all(
          sections.map(async (section) => {
            try {
              const result = await fetchPinsForGroup(board.id, section.id);
              return { section, ...result };
            } catch {
              return { section, pins: [] as Pin[], bookmark: null };
            }
          })
        );

        for (const result of sectionResults) {
          result.pins.forEach(p => sectionPinIds.add(p.id));
          if (result.pins.length > 0) {
            groups.push({
              section: result.section,
              pins: result.pins,
              bookmark: result.bookmark,
              loading: false,
            });
          }
        }
      }

      // Board-level pins minus any that belong to sections = unsectioned pins
      const unsectionedPins = unsectionedRes.pins.filter(
        (p: Pin) => !sectionPinIds.has(p.id)
      );
      if (unsectionedPins.length > 0 || groups.length === 0) {
        groups.unshift({
          section: null,
          pins: unsectionedPins,
          bookmark: unsectionedRes.bookmark,
          loading: false,
        });
      }

      setSectionGroups(groups);
      // Collapse all sections by default
      setCollapsedSections(new Set(
        groups.filter(g => g.section).map(g => g.section!.id)
      ));
    } catch (err) {
      setError('Failed to load pins. Please try again.');
      console.error('Error loading board content:', err);
    } finally {
      setLoadingInitial(false);
    }
  }, [user, fetchPinsForGroup]);

  const loadMorePins = useCallback(async (groupIndex: number) => {
    const group = sectionGroups[groupIndex];
    if (!group?.bookmark || !selectedBoard) return;

    setSectionGroups(prev => prev.map((g, i) =>
      i === groupIndex ? { ...g, loading: true } : g
    ));

    try {
      const result = await fetchPinsForGroup(
        selectedBoard.id,
        group.section?.id ?? null,
        group.bookmark
      );

      setSectionGroups(prev => prev.map((g, i) =>
        i === groupIndex
          ? { ...g, pins: [...g.pins, ...result.pins], bookmark: result.bookmark, loading: false }
          : g
      ));
    } catch (err) {
      console.error('Error loading more pins:', err);
      setSectionGroups(prev => prev.map((g, i) =>
        i === groupIndex ? { ...g, loading: false } : g
      ));
    }
  }, [sectionGroups, selectedBoard, fetchPinsForGroup]);

  // Load boards when connected and tab is first activated
  const [hasActivated, setHasActivated] = useState(false);
  useEffect(() => {
    if (active && !hasActivated) {
      setHasActivated(true);
    }
  }, [active, hasActivated]);

  useEffect(() => {
    if (pinterestConnected && hasActivated && boards.length === 0 && !loadingBoards) {
      fetchBoards();
    }
  }, [pinterestConnected, hasActivated, boards.length, loadingBoards, fetchBoards]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await initiatePinterestConnection(user);
    } catch {
      setError('Failed to connect to Pinterest. Please try again.');
      setConnecting(false);
    }
  };

  const handleSelectBoard = (board: Board) => {
    setSelectedBoard(board);
    setSectionGroups([]);
    setSelectedPinIds(new Set());
    loadBoardContent(board);
  };

  const handleBackToBoards = () => {
    setSelectedBoard(null);
    setSectionGroups([]);
    setSelectedPinIds(new Set());
  };

  const allPins = sectionGroups.flatMap(g => g.pins);
  const selectablePins = allPins.filter(p => !p.alreadyImported);

  const togglePin = (pinId: string) => {
    const pin = allPins.find(p => p.id === pinId);
    if (pin?.alreadyImported) return;

    setSelectedPinIds(prev => {
      const next = new Set(prev);
      if (next.has(pinId)) {
        next.delete(pinId);
      } else {
        next.add(pinId);
      }
      return next;
    });
  };

  const toggleAllPins = () => {
    if (selectedPinIds.size === selectablePins.length) {
      setSelectedPinIds(new Set());
    } else {
      setSelectedPinIds(new Set(selectablePins.map(p => p.id)));
    }
  };

  const handleImport = async () => {
    const selectedPins = allPins
      .filter(p => selectedPinIds.has(p.id))
      .map(p => ({ url: p.link, pinId: p.id }));

    if (selectedPins.length === 0) return;

    setImporting(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pinterest/import`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pins: selectedPins }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (handleUsageLimitError(response, data)) {
          return;
        }
        throw new Error(data.error || 'Import failed');
      }

      const result = await response.json();

      if (result.imported > 0) {
        notifications.show({
          title: `${result.imported} recipe import${result.imported > 1 ? 's' : ''} started`,
          message: result.duplicates > 0
            ? `${result.duplicates} already imported. Recipes will appear in your cookbook when ready.`
            : 'Your recipes are being processed. They will appear in your cookbook when ready.',
          color: 'blue',
          icon: <IconCheck size={18} />,
          autoClose: 7000,
        });
        onImportStarted();
        onClose();
      } else if (result.duplicates > 0) {
        notifications.show({
          title: 'Already imported',
          message: result.duplicates === 1
            ? 'This recipe has already been imported. Check your cookbook to find it.'
            : `All ${result.duplicates} selected recipes have already been imported.`,
          color: 'orange',
          icon: <IconAlertCircle size={18} />,
          autoClose: 7000,
        });
      } else {
        notifications.show({
          title: 'Import failed',
          message: 'Unable to import the selected recipes. Please try again.',
          color: 'red',
          icon: <IconAlertCircle size={18} />,
          autoClose: 7000,
        });
      }
    } catch (err) {
      console.error('Pinterest import error:', err);
      notifications.show({
        title: 'Import failed',
        message: 'Something went wrong. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 7000,
      });
    } finally {
      setImporting(false);
    }
  };

  const renderPinGrid = (pins: Pin[]) => (
    <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
      {pins.map(pin => {
        const isImported = pin.alreadyImported === true;
        return (
        <UnstyledButton
          key={pin.id}
          onClick={() => togglePin(pin.id)}
          style={{
            opacity: importing ? 0.6 : isImported ? 0.45 : 1,
            height: '100%',
            cursor: isImported ? 'default' : undefined,
          }}
          disabled={importing}
        >
          <Paper
            withBorder
            radius="md"
            h="100%"
            style={{
              overflow: 'hidden',
              outline: selectedPinIds.has(pin.id)
                ? '2px solid var(--mantine-color-blue-6)'
                : 'none',
            }}
          >
            {pin.thumbnailUrl ? (
              <Image
                src={pin.thumbnailUrl}
                h={120}
                fit="cover"
                alt={pin.title || 'Pin'}
              />
            ) : (
              <Center h={120} bg="gray.1">
                <IconBrandPinterest size={24} color="#ccc" />
              </Center>
            )}
            <Stack gap={2} p="xs">
              <Text size="xs" fw={500} lineClamp={2}>
                {pin.title || pin.description || 'Untitled pin'}
              </Text>
              <Text size="xs" c="dimmed" lineClamp={1}>
                {new URL(pin.link).hostname.replace(/^www\./, '')}
              </Text>
            </Stack>
          </Paper>
        </UnstyledButton>
      );
      })}
    </SimpleGrid>
  );

  // Not connected state
  if (!pinterestConnected) {
    return (
      <Stack gap="md" mt="md" align="center" py="xl">
        <IconBrandPinterest size={48} color="#E60023" />
        <Text ta="center" maw={350}>
          Connect your Pinterest account to browse your boards and import saved recipes.
        </Text>
        <Button
          color="red"
          onClick={handleConnect}
          loading={connecting}
          size="md"
        >
          Connect Pinterest
        </Button>
      </Stack>
    );
  }

  // Loading boards
  if (loadingBoards && boards.length === 0) {
    return (
      <Center py="xl" mt="md">
        <Loader size="md" />
      </Center>
    );
  }

  // Error state
  if (error && boards.length === 0 && !selectedBoard) {
    return (
      <Stack gap="md" mt="md">
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" radius="lg">
          {error}
        </Alert>
        <Button variant="light" onClick={fetchBoards}>
          Try again
        </Button>
      </Stack>
    );
  }

  // Pin list view
  if (selectedBoard) {
    return (
      <Stack gap="md" mt="md">
        <Group justify="space-between">
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={handleBackToBoards}
              disabled={importing}
            >
              <IconChevronLeft size={18} />
            </ActionIcon>
            <Text fw={500}>{selectedBoard.name}</Text>
          </Group>
          {!loadingInitial && selectablePins.length > 0 && (
            <Checkbox
              label={`Select all (${selectablePins.length})`}
              checked={selectedPinIds.size === selectablePins.length && selectablePins.length > 0}
              indeterminate={selectedPinIds.size > 0 && selectedPinIds.size < selectablePins.length}
              onChange={toggleAllPins}
              disabled={importing}
            />
          )}
        </Group>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" radius="lg">
            {error}
          </Alert>
        )}

        {loadingInitial && (
          <Center py="xl">
            <Loader size="md" />
          </Center>
        )}

        {!loadingInitial && sectionGroups.map((group, groupIndex) => {
          const isSection = !!group.section;
          const isCollapsed = isSection && collapsedSections.has(group.section!.id);
          const selectableSectionPins = group.pins.filter(p => !p.alreadyImported);
          const sectionSelectedCount = selectableSectionPins.filter(p => selectedPinIds.has(p.id)).length;
          const allSectionSelected = sectionSelectedCount === selectableSectionPins.length && selectableSectionPins.length > 0;

          return (
            <Stack key={group.section?.id ?? 'unsectioned'} gap="sm">
              {isSection && sectionGroups.length > 1 && (
                <Group gap="xs">
                  <UnstyledButton onClick={() => {
                    setCollapsedSections(prev => {
                      const next = new Set(prev);
                      if (next.has(group.section!.id)) {
                        next.delete(group.section!.id);
                      } else {
                        next.add(group.section!.id);
                      }
                      return next;
                    });
                  }} style={{ flex: 1 }}>
                    <Group gap={4}>
                      <IconChevronDown
                        size={14}
                        color="var(--mantine-color-dimmed)"
                        style={{
                          transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                          transition: 'transform 200ms ease',
                        }}
                      />
                      <Text fw={600} size="sm" c="dimmed">
                        {group.section!.name} ({sectionSelectedCount > 0 ? `${sectionSelectedCount}/` : ''}{group.pins.length})
                      </Text>
                    </Group>
                  </UnstyledButton>
                  <Checkbox
                    size="xs"
                    checked={allSectionSelected}
                    indeterminate={sectionSelectedCount > 0 && !allSectionSelected}
                    disabled={importing}
                    onChange={() => {
                      setSelectedPinIds(prev => {
                        const next = new Set(prev);
                        if (allSectionSelected) {
                          selectableSectionPins.forEach(p => next.delete(p.id));
                        } else {
                          selectableSectionPins.forEach(p => next.add(p.id));
                        }
                        return next;
                      });
                    }}
                  />
                </Group>
              )}

              <Collapse in={!isCollapsed}>
                <Stack gap="sm" pt="xs">
                  {renderPinGrid(group.pins)}

                  {group.loading && (
                    <Center py="sm">
                      <Loader size="sm" />
                    </Center>
                  )}

                  {group.bookmark && !group.loading && (
                    <Button
                      variant="light"
                      size="xs"
                      onClick={() => loadMorePins(groupIndex)}
                      fullWidth
                    >
                      Load more{group.section ? ` from ${group.section.name}` : ''}
                    </Button>
                  )}
                </Stack>
              </Collapse>
            </Stack>
          );
        })}

        {!loadingInitial && allPins.length === 0 && (
          <Text ta="center" c="dimmed" py="md">
            No pins with recipe links found on this board.
          </Text>
        )}

        {selectedPinIds.size > 0 && (
          <>
            <Divider />
            <Group justify="flex-end">
              <Button
                onClick={handleImport}
                loading={importing}
                size="md"
              >
                Import {selectedPinIds.size} recipe{selectedPinIds.size > 1 ? 's' : ''}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    );
  }

  // Board list view
  return (
    <Stack gap="sm" mt="md">
      <Text size="sm" c="dimmed">
        Select a board to browse recipe pins.
      </Text>

      {boards.length === 0 && !loadingBoards && (
        <Text ta="center" c="dimmed" py="md">
          No boards found on your Pinterest account.
        </Text>
      )}

      <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
        {boards.map(board => (
          <UnstyledButton key={board.id} onClick={() => handleSelectBoard(board)} style={{ height: '100%' }}>
            <Paper withBorder radius="md" h="100%" style={{ overflow: 'hidden' }}>
              {board.thumbnailUrl ? (
                <Image
                  src={board.thumbnailUrl}
                  h={100}
                  fit="cover"
                  alt={board.name}
                />
              ) : (
                <Center h={100} bg="gray.1">
                  <IconBrandPinterest size={24} color="#ccc" />
                </Center>
              )}
              <Stack gap={2} p="xs">
                <Text fw={500} size="xs" lineClamp={2}>{board.name}</Text>
                <Badge variant="light" size="xs" color="gray">
                  {board.pinCount} pins
                </Badge>
              </Stack>
            </Paper>
          </UnstyledButton>
        ))}
      </SimpleGrid>
    </Stack>
  );
};
